// Pixar Whiteboard Ad's script as live's form reads it (spec G14-pixar-whiteboard-ad, PR g1; live's
// `pixar-whiteboard-ad-form/library-view` `constants.ts`, `messages.ts`, `utils.ts` and
// `director/script.ts`, read as a behaviour spec): live's words, the labels of the script's rows
// and the ten blank lines. What keeps the script from approval is the server's
// (`PixarWhiteboardAdDraft.script_issue`), as are the timing plan and each line's words.
import type {
  WhiteboardClip,
  WhiteboardClipTiming,
  WhiteboardEndCard,
  WhiteboardLineRole,
  WhiteboardPresenter,
  WhiteboardRoom,
  WhiteboardScript,
  WhiteboardTimingPlan,
} from "../../../api/generated/model";

/** Live's words (`constants.ts`). */
export const SCRIPT_TOOLTIP =
  "The presenter and the room, designed for your buyer, and ten lines in three clips: the " +
  "problem, the product on the board, the close. Each line has a spoken version, a board " +
  "headline and an icon. Edit anything before approving — the clip lengths follow the words.";
export const WRITE_SCRIPT = "Write the 10 lines yourself";
export const EMPTY_SCRIPT_HINT = "to write the 10 lines from the product, or";
export const EMPTY_SCRIPT_WRITE_HINT = "to write them yourself.";
export const PRESENTER_TITLE = "Presenter";
export const PRESENTER_NOTE = "designed for this product's buyer · the same voice line goes into every clip";
export const PRESENTER_PLACEHOLDERS: Readonly<Record<Exclude<keyof WhiteboardPresenter, "pronoun">, string>> = {
  identity: "Identity: age, look, hair, skin tone, build",
  outfit: "Outfit with clear colors, head to toe, no logos",
  personality: "Personality, a few words",
  voice: "Voice: accent, sex and age, pitch, pace, warmth",
};
export const ROOM_TITLE = "Room";
export const ROOM_NOTE = "designed for this product · the board, stick, camera and positions stay fixed";
export const ROOM_PLACEHOLDERS: Readonly<Record<keyof WhiteboardRoom, string>> = {
  place: "Place, e.g. a bright spa-like bathroom studio",
  description:
    "2-3 sentences: furniture and props, a foreground element, a layered background; nothing " +
    "busy, no text, screens or logos behind the board",
  palette: "3-4 set colors (the board and the pack stay the strongest contrast)",
  light: "Light: bright, clean, direction and source, neutral white",
  why: "Why it fits this buyer and product",
};
export const SPOKEN_TOOLTIP =
  "Exactly what the presenter says: respellings in place, initialisms hyphenated, numbers as words.";
export const SPOKEN_LABEL = "spoken";
export const TEXT_PLACEHOLDER = "The sentence as written";
export const SPOKEN_PLACEHOLDER = "As the presenter says it: respellings in place, numbers as words";
export const HEADLINE_PLACEHOLDER = "HEADLINE IN CAPITALS";
export const ICON_PLACEHOLDER = "icon (one simple object)";
export const PRODUCT_LINE_NOTE = "+ the real pack, lying on the board with its fixed glow";
export const LAST_LINE_NOTE =
  "nothing new on the board: the presenter points back at the product line, then smiles to camera";
export const END_CARD_TITLE = "End card";
export const END_CARD_NOTE = "4 s · silent";
export const END_CARD_PLACEHOLDERS: Readonly<Record<keyof WhiteboardEndCard, string>> = {
  headline: "Headline",
  support_line: "Supporting line",
  cta_button: "Button, e.g. Shop now",
};
export const ROLE_LABELS: Readonly<Record<WhiteboardLineRole, string>> = {
  hook: "hook",
  what_you_do: "what you do",
  the_limit: "the limit",
  the_gap: "the gap",
  bad_result: "bad result",
  product: "product",
  mechanism: "mechanism",
  second_benefit: "second benefit",
  closer: "closer",
  cta: "call to action",
};
export const CLIP_NOTES: Readonly<Record<WhiteboardClip, string>> = {
  A: "clip A · the problem",
  B: "clip B · the bad result, the product on the board, the mechanism",
  C: "clip C · second benefit, closer, call to action at the product line",
};
export const CLIPS: readonly WhiteboardClip[] = ["A", "B", "C"];

/** The line that puts the real pack on the board, and the last (live's `ids.ts`). */
export const PRODUCT_LINE_ID = 6;
export const LAST_LINE_ID = 10;
/** Live's caps the rows count against (`MAX_WORDS_PER_LINE`, `HEADLINE_MAX_CHARS`), and a line's
 * as typed (`MAX_SCRIPT_LINE_LENGTH`). */
export const MAX_WORDS_PER_LINE = 22;
export const HEADLINE_MAX_CHARS = 20;
export const MAX_LINE_LENGTH = 500;

/** Each line's clip and role, in order (live's `LINE_ROLES`, `CLIP_LINE_IDS`). */
const LINES: readonly [WhiteboardClip, WhiteboardLineRole][] = [
  ["A", "hook"],
  ["A", "what_you_do"],
  ["A", "the_limit"],
  ["A", "the_gap"],
  ["B", "bad_result"],
  ["B", "product"],
  ["B", "mechanism"],
  ["C", "second_benefit"],
  ["C", "closer"],
  ["C", "cta"],
];

/** "Write the 10 lines yourself" (live's `toBlankScript`): the ten empty lines in their clips and
 * roles, the presenter "she". */
export function blankScript(): WhiteboardScript {
  return {
    product_name: "",
    pack_type: "",
    problem: "",
    presenter: { pronoun: "she", identity: "", outfit: "", personality: "", voice: "" },
    room: { place: "", description: "", palette: "", light: "", why: "" },
    claims: [],
    lines: LINES.map(([clip, role], index) => ({
      id: index + 1,
      clip,
      role,
      text: "",
      spoken: "",
      claim_ids: [],
      board: { headline: "", icon: "" },
    })),
    end_card: { headline: "", support_line: "", cta_button: "" },
    pronunciations: [],
  };
}

/** "A 14 s · B 14 s · C 15 s · end card 4 s · 47 s" (live's `toScriptMeta`). */
export function scriptMetaLabel(timing: WhiteboardTimingPlan): string {
  return [
    ...timing.clips.map((clip) => `${clip.clip} ${clip.duration_s} s`),
    `end card ${timing.end_card_s} s`,
    `${timing.total_s} s`,
  ].join(" · ");
}

/** "14 s · 38 words · 2.99 w/s" (live's `toClipMeta`). */
export function clipMetaLabel(timing: WhiteboardClipTiming): string {
  return `${timing.duration_s} s · ${timing.words} words · ${timing.pace_wps} w/s`;
}

/** A line's spoken words as the plan counted them; null with no plan yet. */
export function lineWords(timing: WhiteboardTimingPlan | null, lineId: number): number | null {
  return timing?.clips.flatMap((clip) => clip.lines).find((line) => line.line_id === lineId)?.words ?? null;
}

/** The lines say the same words, whatever else of the script differs (live's `hasSameLineTexts`):
 * only an edit of them shows the gate's issue, never a new script. */
export function sameLineTexts(one: WhiteboardScript, other: WhiteboardScript): boolean {
  return (
    one.lines.length === other.lines.length &&
    one.lines.every((line, index) => line.text === other.lines[index]?.text)
  );
}
